import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { api } from '../api';
import { useStrategy } from '../StrategyContext';

import Tip from '../components/Tip';

/* ── Warning text ── */
const Warn: React.FC<{ show: boolean; text: string; color?: string }> = ({ show, text, color = '#eab308' }) =>
  show ? (
    <div style={{ fontSize: 11, color, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontWeight: 700 }}>!</span> {text}
    </div>
  ) : null;

/* ── Number input ── */
const NumInput: React.FC<{
  value: number; onChange: (v: number) => void;
  min: number; max: number; step?: number;
  suffix?: string; prefix?: string; disabled?: boolean; width?: number;
}> = ({ value, onChange, min, max, step = 1, suffix, prefix, disabled, width = 80 }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    {prefix && <span style={{ fontSize: 14, color: '#71717a' }}>{prefix}</span>}
    <input
      type="number"
      className="form-input"
      style={{ width, textAlign: 'center', fontSize: 16, fontWeight: 600 }}
      value={value} min={min} max={max} step={step} disabled={disabled}
      onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) onChange(v); }}
    />
    {suffix && <span style={{ fontSize: 14, color: '#71717a' }}>{suffix}</span>}
  </div>
);

/* ── Toggle switch ── */
const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }> = ({ checked, onChange, disabled }) => (
  <div
    onClick={() => !disabled && onChange(!checked)}
    style={{
      width: 36, height: 20, background: checked ? '#22c55e' : '#3f3f46',
      borderRadius: 10, position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer', transition: 'background 0.2s',
    }}
  >
    <div style={{
      position: 'absolute', top: 2, width: 16, height: 16, background: 'white', borderRadius: '50%',
      transition: 'left 0.2s, right 0.2s',
      ...(checked ? { right: 2 } : { left: 2 }),
    }} />
  </div>
);

/* ── Tooltip copy per parameter ── */
const tips: Record<string, string> = {
  // Entry (Long)
  donchian_period: 'A buy signal triggers when price closes above the highest high of the last N days. Higher values = fewer but stronger breakout signals.',
  volume_mult: 'Requires trading volume to be N times the 20-day average. Filters out low-conviction breakouts — higher values mean stricter confirmation.',
  ema_period: 'Price must be above the Exponential Moving Average. Ensures you only buy when the short-term trend is up.',
  bull_filter: 'Blocks all new entries when Bitcoin is in a downtrend. Requires BTC price above its 200-day average AND the 50-day above the 200-day (golden cross). Dramatically reduces losses during bear markets.',
  pyramid_enabled: 'Adds to a winning position when it makes a new breakout. Lets winners grow larger while keeping losers small.',
  pyramid_trigger: 'Position must be up this percentage before a pyramid add is considered. Ensures you only add to confirmed winners.',
  pyramid_risk: 'Risk allocated to the pyramid add — typically half of the initial position risk. Limits total exposure on any single trade.',
  atr_mult: 'Primary exit method. The stop trails behind the highest price by N times the Average True Range (14-day). A wider multiplier gives trades more room to breathe but gives back more profit on reversals.',
  exit_period: 'Secondary exit. If price closes below the lowest low of the last N days, the position exits. Catches reversals that the trailing stop may miss.',
  emergency_stop: 'Hard safety limit. If a position drops this far below entry price, it exits immediately regardless of other signals. Protects against flash crashes and gap downs.',
  blowoff: 'Automatic risk tightening during euphoria. When volume spikes above 3x average and RSI exceeds 80, the trailing stop tightens to 1.5x ATR to lock in gains before a potential crash.',
  tp1: 'Locks in partial profit early. Reduces risk while keeping most of the position for larger moves.',
  tp2: 'Sells another portion at a higher gain. By this point, a significant amount of the original position has been secured at profit.',
  runner: 'The remaining percentage rides the trailing stop with no fixed target. This is where the big wins come from — letting winners run until the trend actually ends.',
  // Futures Long
  futures_long_enabled: 'Enable leveraged long positions via Coinbase CFM perpetual futures. Uses the same Donchian breakout signals but with configurable leverage.',
  futures_long_leverage: 'Multiplies position size. 2x leverage = double exposure, double risk. Start at 1x until proven profitable.',
  futures_long_entry_period: 'A buy signal triggers when price closes above the highest high of the last N days. Same logic as spot but independently configurable.',
  futures_long_volume_mult: 'Requires trading volume to be N times the 20-day average. Filters out low-conviction breakouts.',
  futures_long_atr_mult: 'Trailing stop distance. Trails behind the highest price by N times ATR(14). Same mechanics as spot longs.',
  futures_long_exit_period: 'If price closes below the lowest low of the last N days, the position exits.',
  futures_long_emergency_stop: 'Hard safety limit below entry. With leverage, this is especially important.',
  futures_long_pyramid: 'Add to a winning leveraged long on new breakout.',
  futures_long_tp1: 'Lock in partial profit early on leveraged long positions.',
  futures_long_tp2: 'Sell another portion at a higher gain on leveraged longs.',
  futures_long_runner: 'Remaining leveraged long rides the trailing stop.',
  futures_long_risk: 'Percentage of total equity risked per leveraged long trade. With leverage, effective exposure is multiplied.',
  // Short / Futures
  short_enabled: 'Master toggle for futures short trading. When disabled, the bot will not open any new short positions.',
  short_leverage: 'Multiplies short position size. 2x leverage = double exposure, double risk. Short squeezes are violent — use caution.',
  bear_filter: 'Blocks short entries unless Bitcoin is in a confirmed death cross: SMA(50) below SMA(200) AND BTC below SMA(200). Critical safety filter — without it, shorts get stopped out in bull markets.',
  short_entry_period: 'A short signal triggers when price closes below the lowest low of the last N days. Shorter periods = more frequent entries.',
  short_volume_mult: 'Requires selling volume to be N times the 20-day average for shorts. Higher = stricter, filters noise.',
  short_atr_mult: 'Inverted trailing stop — trails above the lowest price by N times ATR(14). Tighter for shorts since bear moves are faster.',
  short_exit_period: 'If price closes above the highest high of the last N days, the short exits. Catches trend reversals.',
  short_emergency_stop: 'Hard ceiling above entry price. If a short position rises this far, it exits immediately.',
  short_max_hold: 'Maximum days to hold a short position. Bear moves tend to be shorter — this prevents shorts from dragging.',
  short_tp1: 'Locks in partial profit when price drops this far. Reduces risk on the short position.',
  short_tp2: 'Covers another portion at a larger drop. Secures more profit while keeping a runner.',
  short_runner: 'The remaining short position rides the inverted trailing stop until the downtrend ends.',
  short_risk: 'Percentage of total equity risked per short trade. Same sizing logic as longs but applied to short stop distance.',
  // Position sizing
  risk_per_trade: 'Percentage of total equity risked per trade. The bot calculates position size based on the distance to the stop loss — tighter stops mean larger positions, wider stops mean smaller ones.',
  max_positions: 'Maximum number of open positions at any time. Limits total portfolio exposure and ensures enough capital for each position.',
  starting_capital: 'Initial paper trading balance. All equity calculations and position sizing start from this amount.',
};

/* Fields editable on each tab — used by Reset Defaults */
const tabFields: Record<string, string[]> = {
  spot: ['bull_filter_enabled', 'pyramid_enabled', 'pyramid_gain_pct', 'pyramid_risk_pct',
    'atr_mult', 'exit_period', 'emergency_stop_pct', 'tp1_size_pct', 'tp1_gain_pct', 'tp2_size_pct', 'tp2_gain_pct'],
  'futures-long': ['futures_long_enabled', 'futures_long_leverage', 'futures_long_atr_mult',
    'futures_long_entry_period', 'futures_long_exit_period', 'futures_long_volume_mult',
    'futures_long_emergency_stop_pct', 'futures_long_risk_per_trade_pct',
    'futures_long_pyramid_enabled', 'futures_long_pyramid_gain_pct', 'futures_long_pyramid_risk_pct',
    'futures_long_tp1_size_pct', 'futures_long_tp1_gain_pct', 'futures_long_tp2_size_pct', 'futures_long_tp2_gain_pct'],
  'futures-short': ['short_enabled', 'short_leverage', 'bear_filter_enabled', 'short_atr_mult',
    'short_entry_period', 'short_exit_period', 'short_volume_mult', 'short_emergency_stop_pct',
    'short_max_hold_days', 'short_risk_per_trade_pct',
    'short_tp1_size_pct', 'short_tp1_gain_pct', 'short_tp2_size_pct', 'short_tp2_gain_pct'],
  sizing: ['risk_per_trade_pct', 'max_positions', 'pyramid_risk_pct', 'starting_capital'],
};

const Strategy: React.FC<{ isAdmin?: boolean }> = ({ isAdmin = false }) => {
  const { strategy } = useStrategy();
  const [activeTab, setActiveTab] = useState('spot');
  const [config, setConfig] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [isDemo, setIsDemo] = useState(false);

  useEffect(() => {
    setConfig(null);
    setDirty(false);
    api.getConfig(strategy).then(setConfig).catch(console.error);
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsDemo(session?.user?.email === 'demo@tradesavvy.io');
    });
  }, [strategy]);

  const readOnly = isDemo || !isAdmin;

  const updateField = (field: string, value: any) => {
    setConfig((prev: any) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const updateStrategyParam = (field: string, value: any) => {
    setConfig((prev: any) => ({
      ...prev,
      strategy_params: { ...(prev.strategy_params || {}), [field]: value },
    }));
    setDirty(true);
  };

  const sp = config?.strategy_params || {};

  const handleSave = async () => {
    if (!config || !dirty || readOnly) return;
    if (isDemo) {
      alert('Demo account cannot modify settings. Sign up for your own account to customize.');
      return;
    }
    setSaving(true);
    try {
      const result = await api.updateConfig({ ...config, strategy });
      setConfig(result);
      setDirty(false);
    } catch (err: any) {
      console.error('Save error:', err);
      const msg = err.message || '';
      const match = msg.match(/API \d+: (.*)/);
      if (match) {
        try {
          const parsed = JSON.parse(match[1]);
          alert(parsed.detail || 'Failed to save configuration');
        } catch {
          alert(match[1]);
        }
      } else {
        alert('Failed to save configuration');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const fields = tabFields[activeTab];
    if (!fields) return;
    api.getConfigDefaults(strategy).then((defaults: any) => {
      setConfig((prev: any) => {
        const updated = { ...prev };
        for (const f of fields) {
          if (f in defaults) updated[f] = defaults[f];
        }
        return updated;
      });
      setDirty(true);
    }).catch(console.error);
  };

  if (!config) {
    return <div style={{ textAlign: 'center', padding: 48, color: '#71717a' }}>Loading configuration...</div>;
  }

  const coins = config.coins || [];
  const coinSymbols = coins.map((c: string) => c.replace('-USD', ''));
  const shortCoins = config.short_coins || [];
  const shortCoinSymbols = shortCoins.map((c: string) => c.replace('-USD', ''));
  const flCoins = config.futures_long_coins || [];
  const flCoinSymbols = flCoins.map((c: string) => c.replace('-USD', ''));
  const runnerPct = 100 - (config.tp1_size_pct ?? 25) - (config.tp2_size_pct ?? 25);
  const shortRunnerPct = 100 - (config.short_tp1_size_pct ?? 25) - (config.short_tp2_size_pct ?? 25);
  const flRunnerPct = 100 - (config.futures_long_tp1_size_pct ?? 25) - (config.futures_long_tp2_size_pct ?? 25);
  const shortDisabled = readOnly || !config.short_enabled;
  const flDisabled = readOnly || !config.futures_long_enabled;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Strategy Configuration</div>
        {readOnly && !isDemo && (
          <div style={{ fontSize: 13, color: '#71717a', padding: '6px 12px', background: '#1c1c22', borderRadius: 6, border: '1px solid #2a2d35' }}>
            View only — contact admin to request changes
          </div>
        )}
        {!readOnly && (
          <div className="header-actions">
            <button className="btn" onClick={handleReset}>Reset Defaults</button>
            <button className={`btn btn-primary ${!dirty ? 'btn' : ''}`} onClick={handleSave} disabled={saving || !dirty}>
              {saving ? 'Saving...' : dirty ? 'Save & Deploy' : 'Saved'}
            </button>
          </div>
        )}
      </div>

      <div className="tabs">
        <div className={`tab ${activeTab === 'spot' ? 'active' : ''}`} onClick={() => setActiveTab('spot')}>Spot (Long)</div>
        <div className={`tab ${activeTab === 'futures-long' ? 'active' : ''}`} onClick={() => setActiveTab('futures-long')}>Futures Long</div>
        <div className={`tab ${activeTab === 'futures-short' ? 'active' : ''}`} onClick={() => setActiveTab('futures-short')}>Futures Short</div>
        <div className={`tab ${activeTab === 'sizing' ? 'active' : ''}`} onClick={() => setActiveTab('sizing')}>Sizing</div>
        <div className={`tab ${activeTab === 'coins' ? 'active' : ''}`} onClick={() => setActiveTab('coins')}>Coins</div>
      </div>

      {/* ── Spot (Long) ── */}
      {activeTab === 'spot' && strategy === 'ualgo' && (
        <div className="grid-equal">
          <div className="card">
            <div className="card-header">
              <div className="card-title">UAlgo Entry Conditions</div>
            </div>
            <div className="card-body">
              <div className="config-grid">
                <div className="config-item">
                  <div className="config-label">Supertrend ATR Period <Tip text="ATR period used by Supertrend indicator for trend detection" /></div>
                  <NumInput value={sp.supertrend_atr_period ?? 14} onChange={v => updateStrategyParam('supertrend_atr_period', Math.round(v))} min={7} max={30} step={1} suffix="bars" disabled={readOnly} />
                </div>
                <div className="config-item">
                  <div className="config-label">Supertrend Multiplier <Tip text="ATR multiplier for Supertrend bands. Higher = less sensitive to noise" /></div>
                  <NumInput value={sp.supertrend_multiplier ?? 2.0} onChange={v => updateStrategyParam('supertrend_multiplier', v)} min={1.0} max={5.0} step={0.1} suffix="x" disabled={readOnly} />
                </div>
                <div className="config-item">
                  <div className="config-label">Flip Window <Tip text="Supertrend must have flipped direction within this many bars for entry" /></div>
                  <NumInput value={sp.flip_window ?? 5} onChange={v => updateStrategyParam('flip_window', Math.round(v))} min={1} max={15} step={1} suffix="bars" disabled={readOnly} />
                </div>
                <div className="config-item">
                  <div className="config-label">RSI Period <Tip text="Relative Strength Index lookback period" /></div>
                  <NumInput value={sp.rsi_period ?? 9} onChange={v => updateStrategyParam('rsi_period', Math.round(v))} min={5} max={21} step={1} suffix="bars" disabled={readOnly} />
                </div>
                <div className="config-item">
                  <div className="config-label">RSI Buy Threshold <Tip text="RSI must be below this value for long entries (oversold confirmation)" /></div>
                  <NumInput value={sp.rsi_buy_threshold ?? 60} onChange={v => updateStrategyParam('rsi_buy_threshold', Math.round(v))} min={30} max={80} step={5} disabled={readOnly} />
                </div>
                <div className="config-item">
                  <div className="config-label">CMO Period <Tip text="Chande Momentum Oscillator lookback period" /></div>
                  <NumInput value={sp.cmo_period ?? 14} onChange={v => updateStrategyParam('cmo_period', Math.round(v))} min={5} max={30} step={1} suffix="bars" disabled={readOnly} />
                </div>
                <div className="config-item">
                  <div className="config-label">CMO Buy Threshold <Tip text="CMO must be above this value for long entries (momentum confirmation)" /></div>
                  <NumInput value={sp.cmo_buy_threshold ?? -10} onChange={v => updateStrategyParam('cmo_buy_threshold', Math.round(v))} min={-50} max={50} step={5} disabled={readOnly} />
                </div>
                <div className="config-item">
                  <div className="config-label">HMA Open Period <Tip text="Hull Moving Average fast period for entry momentum" /></div>
                  <NumInput value={sp.hma_open_period ?? 5} onChange={v => updateStrategyParam('hma_open_period', Math.round(v))} min={3} max={15} step={1} suffix="bars" disabled={readOnly} />
                </div>
                <div className="config-item">
                  <div className="config-label">HMA Close Period <Tip text="Hull Moving Average slow period for exit momentum" /></div>
                  <NumInput value={sp.hma_close_period ?? 12} onChange={v => updateStrategyParam('hma_close_period', Math.round(v))} min={5} max={30} step={1} suffix="bars" disabled={readOnly} />
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Exit Rules</div>
            </div>
            <div className="card-body">
              <div className="config-grid">
                <div className="config-item">
                  <div className="config-label">Stop Loss <Tip text="Fixed percentage stop loss below entry. UAlgo uses fixed stops, not trailing." /></div>
                  <NumInput value={sp.sl_pct ?? 2.0} onChange={v => updateStrategyParam('sl_pct', v)} min={0.5} max={10} step={0.5} suffix="%" disabled={readOnly} />
                  <div className="config-desc">Fixed stop below entry price</div>
                </div>
                <div className="config-item">
                  <div className="config-label">TP1 Fraction <Tip text="Sell this fraction at 1R profit (1x risk-reward)" /></div>
                  <NumInput value={(sp.tp1_fraction ?? 0.25) * 100} onChange={v => updateStrategyParam('tp1_fraction', v / 100)} min={5} max={50} step={5} suffix="% at 1R" disabled={readOnly} />
                </div>
                <div className="config-item">
                  <div className="config-label">TP2 Fraction <Tip text="Sell this fraction at 2R profit (2x risk-reward)" /></div>
                  <NumInput value={(sp.tp2_fraction ?? 0.25) * 100} onChange={v => updateStrategyParam('tp2_fraction', v / 100)} min={5} max={50} step={5} suffix="% at 2R" disabled={readOnly} />
                </div>
                <div className="config-item">
                  <div className="config-label">TP3 (Runner) <Tip text="Remaining fraction rides until 3R or opposing signal" /></div>
                  <div className="config-value">{((sp.tp3_fraction ?? 0.50) * 100).toFixed(0)}% at 3R</div>
                  <div className="config-desc">Remainder after TP1 + TP2</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Bull Market Filter</div>
            </div>
            <div className="card-body">
              <div className="config-grid">
                <div className="config-item">
                  <div className="config-label">Bull Filter <Tip text={tips.bull_filter} /></div>
                  <Toggle checked={config.bull_filter_enabled} onChange={v => updateField('bull_filter_enabled', v)} disabled={readOnly} />
                  <div className="config-desc">
                    {config.bull_filter_enabled
                      ? 'Entries blocked when BTC is in a downtrend'
                      : 'Entries allowed regardless of BTC trend'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'spot' && strategy === 'donchian' && (
        <div className="grid-equal">
          <div className="card">
            <div className="card-header">
              <div className="card-title">Entry Conditions</div>
              <span className="badge badge-gray">Locked</span>
            </div>
            <div className="card-body">
              <div className="config-grid">
                <div className="config-item">
                  <div className="config-label">Donchian Period <Tip text={tips.donchian_period} /></div>
                  <div className="config-value">{config.donchian_period} days</div>
                  <div className="config-desc">Breakout above N-day high</div>
                </div>
                <div className="config-item">
                  <div className="config-label">Volume Multiplier <Tip text={tips.volume_mult} /></div>
                  <div className="config-value">{config.volume_mult}x</div>
                  <div className="config-desc">Volume must exceed N x 20-day avg</div>
                </div>
                <div className="config-item">
                  <div className="config-label">EMA Trend Filter <Tip text={tips.ema_period} /></div>
                  <div className="config-value">{config.ema_period}-day</div>
                  <div className="config-desc">Price must be above EMA(N)</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Bull Market Filter</div>
            </div>
            <div className="card-body">
              <div className="config-grid">
                <div className="config-item">
                  <div className="config-label">Bull Filter <Tip text={tips.bull_filter} /></div>
                  <Toggle checked={config.bull_filter_enabled} onChange={v => updateField('bull_filter_enabled', v)} disabled={readOnly} />
                  <div className="config-desc">
                    {config.bull_filter_enabled
                      ? 'Entries blocked when BTC is in a downtrend'
                      : 'Entries allowed regardless of BTC trend'}
                  </div>
                  <Warn
                    show={!config.bull_filter_enabled}
                    text="Bull filter disabled — entries allowed during bear markets. Backtests show significantly higher drawdowns without this filter (20% vs 15% max DD)."
                    color="#ef4444"
                  />
                </div>
                <div className="config-item">
                  <div className="config-label">How It Works</div>
                  <div style={{ fontSize: 13, color: '#a1a1aa', lineHeight: 1.5 }}>
                    Requires BTC close &gt; SMA(200) <strong style={{ color: '#e4e4e7' }}>and</strong> SMA(50) &gt; SMA(200) (golden cross).
                    When conditions are not met, all new entries and pyramid adds are blocked.
                  </div>
                  <div className="config-desc">See Dashboard for current BULL / BEAR status</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ gridColumn: '1 / -1' }}>
            <div className="card-header">
              <div className="card-title">Pyramiding</div>
            </div>
            <div className="card-body">
              <div className="config-grid">
                <div className="config-item">
                  <div className="config-label">Pyramiding <Tip text={tips.pyramid_enabled} /></div>
                  <Toggle checked={config.pyramid_enabled} onChange={v => updateField('pyramid_enabled', v)} disabled={readOnly} />
                  <div className="config-desc">Add to winners on new breakout</div>
                </div>
                <div className="config-item">
                  <div className="config-label">Trigger <Tip text={tips.pyramid_trigger} /></div>
                  <NumInput
                    value={config.pyramid_gain_pct} onChange={v => updateField('pyramid_gain_pct', v)}
                    min={5} max={50} step={1} prefix="+" suffix="%"
                    disabled={isDemo || !config.pyramid_enabled}
                  />
                  <div className="config-desc">Min gain before adding</div>
                </div>
                <div className="config-item">
                  <div className="config-label">Pyramid Risk <Tip text={tips.pyramid_risk} /></div>
                  <NumInput
                    value={config.pyramid_risk_pct} onChange={v => updateField('pyramid_risk_pct', v)}
                    min={0.5} max={5} step={0.5} suffix="%"
                    disabled={isDemo || !config.pyramid_enabled}
                  />
                  <div className="config-desc">Risk on add (half of initial)</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ gridColumn: '1 / -1' }}>
            <div className="card-header">
              <div className="card-title">Exit Rules</div>
            </div>
            <div className="card-body">
              <div className="config-grid">
                <div className="config-item">
                  <div className="config-label">ATR Trailing Stop <Tip text={tips.atr_mult} /></div>
                  <NumInput
                    value={config.atr_mult} onChange={v => updateField('atr_mult', v)}
                    min={1.5} max={8} step={0.5} suffix="x ATR(14)" disabled={readOnly}
                  />
                  <div className="config-desc">Primary exit — trails behind highest price</div>
                </div>
                <div className="config-item">
                  <div className="config-label">Donchian Exit Period <Tip text={tips.exit_period} /></div>
                  <NumInput
                    value={config.exit_period} onChange={v => updateField('exit_period', Math.round(v))}
                    min={5} max={20} step={1} suffix="days" disabled={readOnly}
                  />
                  <div className="config-desc">Close below the N-day lowest low</div>
                </div>
                <div className="config-item">
                  <div className="config-label">Emergency Stop <Tip text={tips.emergency_stop} /></div>
                  <NumInput
                    value={config.emergency_stop_pct} onChange={v => updateField('emergency_stop_pct', v)}
                    min={5} max={30} step={1} suffix="%" disabled={readOnly}
                  />
                  <div className="config-desc">Hard floor below entry price</div>
                  <Warn show={config.emergency_stop_pct < 10} text="Very tight — may cause premature exits" />
                  <Warn show={config.emergency_stop_pct > 25} text="Very wide — limited downside protection" />
                </div>
                <div className="config-item">
                  <div className="config-label">
                    Blow-off Top <Tip text={tips.blowoff} />
                    <span className="badge badge-gray" style={{ marginLeft: 6 }}>Locked</span>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#22c55e' }}>Enabled</div>
                  <div className="config-desc">Tighten to 1.5x ATR when vol &gt; 3x AND RSI &gt; 80</div>
                </div>
                <div className="config-item">
                  <div className="config-label">Partial TP1 <Tip text={tips.tp1} /></div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <NumInput
                      value={config.tp1_size_pct ?? 25} onChange={v => updateField('tp1_size_pct', v)}
                      min={5} max={50} step={5} suffix="%" disabled={readOnly} width={65}
                    />
                    <span style={{ color: '#71717a', fontSize: 13 }}>at</span>
                    <NumInput
                      value={config.tp1_gain_pct ?? 10} onChange={v => updateField('tp1_gain_pct', v)}
                      min={3} max={50} step={1} prefix="+" suffix="% gain" disabled={readOnly} width={65}
                    />
                  </div>
                </div>
                <div className="config-item">
                  <div className="config-label">Partial TP2 <Tip text={tips.tp2} /></div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <NumInput
                      value={config.tp2_size_pct ?? 25} onChange={v => updateField('tp2_size_pct', v)}
                      min={5} max={50} step={5} suffix="%" disabled={readOnly} width={65}
                    />
                    <span style={{ color: '#71717a', fontSize: 13 }}>at</span>
                    <NumInput
                      value={config.tp2_gain_pct ?? 25} onChange={v => updateField('tp2_gain_pct', v)}
                      min={5} max={100} step={1} prefix="+" suffix="% gain" disabled={readOnly} width={65}
                    />
                  </div>
                  <Warn show={(config.tp1_gain_pct ?? 10) >= (config.tp2_gain_pct ?? 25)} text="TP1 target must be less than TP2 target" color="#ef4444" />
                </div>
                <div className="config-item" style={{ gridColumn: '1 / -1' }}>
                  <div className="config-label">Runner <Tip text={tips.runner} /></div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#22c55e' }}>
                    {runnerPct}%
                    <span style={{ fontSize: 13, fontWeight: 400, color: '#71717a', marginLeft: 8 }}>rides trailing stop</span>
                  </div>
                  <Warn show={runnerPct < 20} text="Runner below 20% limits upside potential" />
                  <Warn show={runnerPct <= 0} text="TP1 + TP2 must total less than 100%" color="#ef4444" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── UAlgo Futures Long ── */}
      {activeTab === 'futures-long' && strategy === 'ualgo' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#e4e4e7' }}>
                  Enable Futures Long Trading <Tip text={tips.futures_long_enabled} />
                </div>
                <div style={{ fontSize: 13, color: '#a1a1aa', marginTop: 2 }}>
                  {config.futures_long_enabled ? 'UAlgo long signals also execute via Coinbase CFM perpetual futures' : 'Disabled — only spot long trades will execute.'}
                </div>
              </div>
              <Toggle checked={config.futures_long_enabled} onChange={v => updateField('futures_long_enabled', v)} disabled={readOnly} />
            </div>
          </div>
          <div className="card">
            <div className="card-header"><div className="card-title">Futures Long Settings</div></div>
            <div className="card-body">
              <div className="config-grid">
                <div className="config-item">
                  <div className="config-label">Leverage <Tip text={tips.futures_long_leverage} /></div>
                  <NumInput value={config.futures_long_leverage ?? 1} onChange={v => updateField('futures_long_leverage', v)} min={1} max={3} step={0.5} suffix="x" disabled={readOnly || !config.futures_long_enabled} />
                </div>
                <div className="config-item">
                  <div className="config-label">Risk Per Trade <Tip text={tips.futures_long_risk} /></div>
                  <NumInput value={config.futures_long_risk_per_trade_pct ?? 2} onChange={v => updateField('futures_long_risk_per_trade_pct', v)} min={0.5} max={5} step={0.5} suffix="%" disabled={readOnly || !config.futures_long_enabled} />
                </div>
                <div className="config-item" style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: 13, color: '#a1a1aa', lineHeight: 1.6, padding: '8px 0' }}>
                    UAlgo futures long uses the <strong style={{ color: '#e4e4e7' }}>same entry/exit signals</strong> as the Spot tab (Supertrend + RSI + CMO).
                    Configure signal parameters on the <strong style={{ color: '#8b5cf6' }}>Spot (Long)</strong> tab.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Donchian Futures Long ── */}
      {activeTab === 'futures-long' && strategy === 'donchian' && (
        <div>
          {/* Master toggle */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#e4e4e7' }}>
                  Enable Futures Long Trading <Tip text={tips.futures_long_enabled} />
                </div>
                <div style={{ fontSize: 13, color: '#a1a1aa', marginTop: 2 }}>
                  {config.futures_long_enabled
                    ? 'Bot will open leveraged long positions via Coinbase CFM perpetual futures'
                    : 'Disabled — bot will only use spot for long entries. Enable to add leveraged longs.'}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: config.futures_long_enabled ? '#3b82f6' : '#71717a', fontWeight: 600 }}>
                  {config.futures_long_enabled ? 'ON' : 'OFF'}
                </span>
                <Toggle checked={config.futures_long_enabled ?? false} onChange={v => updateField('futures_long_enabled', v)} disabled={readOnly} />
              </div>
            </div>
            <Warn show={!config.futures_long_enabled} text="Futures long trading disabled. Enable to trade leveraged long positions via Coinbase CFM." />
            <Warn show={config.futures_long_enabled && (config.futures_long_leverage ?? 1) >= 2} text="Leveraged trading amplifies both gains and losses. Ensure your risk management is appropriate." />
          </div>

          <div className="grid-equal">
            {/* Leverage */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">Leverage</div>
              </div>
              <div className="card-body" style={{ opacity: flDisabled ? 0.5 : 1 }}>
                <div className="config-grid">
                  <div className="config-item">
                    <div className="config-label">Leverage Multiplier <Tip text={tips.futures_long_leverage} /></div>
                    <NumInput
                      value={config.futures_long_leverage ?? 1.0} onChange={v => updateField('futures_long_leverage', v)}
                      min={1.0} max={3.0} step={0.5} suffix="x" disabled={flDisabled}
                    />
                    <div className="config-desc">Position size multiplier (1x = no leverage)</div>
                    <Warn show={(config.futures_long_leverage ?? 1) >= 2 && (config.futures_long_leverage ?? 1) < 3} text="2x leverage doubles your exposure and risk" />
                    <Warn show={(config.futures_long_leverage ?? 1) >= 3} text="Maximum leverage — 3x exposure. Only for high-conviction setups." color="#ef4444" />
                  </div>
                  <div className="config-item">
                    <div className="config-label">Exchange</div>
                    <div className="config-value" style={{ fontSize: 14 }}>Coinbase CFM</div>
                    <div className="config-desc">CFTC-regulated perpetual futures, 0.06% taker fee, isolated margin</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Entry Conditions */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">Entry Conditions</div>
              </div>
              <div className="card-body" style={{ opacity: flDisabled ? 0.5 : 1 }}>
                <div className="config-grid">
                  <div className="config-item">
                    <div className="config-label">Donchian Period <Tip text={tips.futures_long_entry_period} /></div>
                    <NumInput
                      value={config.futures_long_entry_period ?? 20} onChange={v => updateField('futures_long_entry_period', Math.round(v))}
                      min={10} max={30} step={1} suffix="days" disabled={flDisabled}
                    />
                    <div className="config-desc">Breakout above N-day high</div>
                  </div>
                  <div className="config-item">
                    <div className="config-label">Volume Multiplier <Tip text={tips.futures_long_volume_mult} /></div>
                    <NumInput
                      value={config.futures_long_volume_mult ?? 1.5} onChange={v => updateField('futures_long_volume_mult', v)}
                      min={1.0} max={5.0} step={0.1} suffix="x" disabled={flDisabled}
                    />
                    <div className="config-desc">Volume must exceed N x 20-day avg</div>
                  </div>
                  <div className="config-item">
                    <div className="config-label">EMA Trend Filter</div>
                    <div className="config-value">21-day</div>
                    <div className="config-desc">Price must be above EMA(21)</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bull Market Filter */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">Bull Market Filter</div>
                <span className="badge badge-gray">Shared with Spot</span>
              </div>
              <div className="card-body" style={{ opacity: flDisabled ? 0.5 : 1 }}>
                <div className="config-grid">
                  <div className="config-item">
                    <div className="config-label">Bull Filter</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: config.bull_filter_enabled ? '#22c55e' : '#ef4444' }}>
                      {config.bull_filter_enabled ? 'Enabled' : 'Disabled'}
                    </div>
                    <div className="config-desc">Same BTC golden cross filter as Spot — gates all long entries (configure on Spot tab)</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Pyramiding */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">Pyramiding</div>
              </div>
              <div className="card-body" style={{ opacity: flDisabled ? 0.5 : 1 }}>
                <div className="config-grid">
                  <div className="config-item">
                    <div className="config-label">Pyramiding <Tip text={tips.futures_long_pyramid} /></div>
                    <Toggle checked={config.futures_long_pyramid_enabled ?? true} onChange={v => updateField('futures_long_pyramid_enabled', v)} disabled={flDisabled} />
                    <div className="config-desc">Add to winners on new breakout</div>
                  </div>
                  <div className="config-item">
                    <div className="config-label">Trigger</div>
                    <NumInput
                      value={config.futures_long_pyramid_gain_pct ?? 15} onChange={v => updateField('futures_long_pyramid_gain_pct', v)}
                      min={5} max={50} step={1} prefix="+" suffix="%"
                      disabled={flDisabled || !(config.futures_long_pyramid_enabled ?? true)}
                    />
                    <div className="config-desc">Min gain before adding</div>
                  </div>
                  <div className="config-item">
                    <div className="config-label">Pyramid Risk</div>
                    <NumInput
                      value={config.futures_long_pyramid_risk_pct ?? 1.0} onChange={v => updateField('futures_long_pyramid_risk_pct', v)}
                      min={0.5} max={5} step={0.5} suffix="%"
                      disabled={flDisabled || !(config.futures_long_pyramid_enabled ?? true)}
                    />
                    <div className="config-desc">Risk on add (half of initial)</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Exit Rules */}
            <div className="card" style={{ gridColumn: '1 / -1' }}>
              <div className="card-header">
                <div className="card-title">Exit Rules</div>
              </div>
              <div className="card-body" style={{ opacity: flDisabled ? 0.5 : 1 }}>
                <div className="config-grid">
                  <div className="config-item">
                    <div className="config-label">ATR Trailing Stop <Tip text={tips.futures_long_atr_mult} /></div>
                    <NumInput
                      value={config.futures_long_atr_mult ?? 4.5} onChange={v => updateField('futures_long_atr_mult', v)}
                      min={1.5} max={8} step={0.5} suffix="x ATR(14)" disabled={flDisabled}
                    />
                    <div className="config-desc">Primary exit — trails behind highest price</div>
                  </div>
                  <div className="config-item">
                    <div className="config-label">Donchian Exit Period <Tip text={tips.futures_long_exit_period} /></div>
                    <NumInput
                      value={config.futures_long_exit_period ?? 10} onChange={v => updateField('futures_long_exit_period', Math.round(v))}
                      min={5} max={20} step={1} suffix="days" disabled={flDisabled}
                    />
                    <div className="config-desc">Close below the N-day lowest low</div>
                  </div>
                  <div className="config-item">
                    <div className="config-label">Emergency Stop <Tip text={tips.futures_long_emergency_stop} /></div>
                    <NumInput
                      value={config.futures_long_emergency_stop_pct ?? 18} onChange={v => updateField('futures_long_emergency_stop_pct', v)}
                      min={5} max={30} step={1} suffix="%" disabled={flDisabled}
                    />
                    <div className="config-desc">Hard floor below entry price</div>
                    <Warn show={(config.futures_long_emergency_stop_pct ?? 18) < 10} text="Very tight — may cause premature exits" />
                    <Warn show={(config.futures_long_leverage ?? 1) >= 2 && (config.futures_long_emergency_stop_pct ?? 18) > 20} text="Wide stop with leverage — potential for large losses" color="#ef4444" />
                  </div>
                  <div className="config-item">
                    <div className="config-label">
                      Blow-off Top <Tip text={tips.blowoff} />
                      <span className="badge badge-gray" style={{ marginLeft: 6 }}>Locked</span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#22c55e' }}>Enabled</div>
                    <div className="config-desc">Tighten to 1.5x ATR when vol &gt; 3x AND RSI &gt; 80</div>
                  </div>
                  <div className="config-item">
                    <div className="config-label">Partial TP1 <Tip text={tips.futures_long_tp1} /></div>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <NumInput
                        value={config.futures_long_tp1_size_pct ?? 25} onChange={v => updateField('futures_long_tp1_size_pct', v)}
                        min={5} max={50} step={5} suffix="%" disabled={flDisabled} width={65}
                      />
                      <span style={{ color: '#71717a', fontSize: 13 }}>at</span>
                      <NumInput
                        value={config.futures_long_tp1_gain_pct ?? 10} onChange={v => updateField('futures_long_tp1_gain_pct', v)}
                        min={3} max={50} step={1} prefix="+" suffix="% gain" disabled={flDisabled} width={65}
                      />
                    </div>
                  </div>
                  <div className="config-item">
                    <div className="config-label">Partial TP2 <Tip text={tips.futures_long_tp2} /></div>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <NumInput
                        value={config.futures_long_tp2_size_pct ?? 25} onChange={v => updateField('futures_long_tp2_size_pct', v)}
                        min={5} max={50} step={5} suffix="%" disabled={flDisabled} width={65}
                      />
                      <span style={{ color: '#71717a', fontSize: 13 }}>at</span>
                      <NumInput
                        value={config.futures_long_tp2_gain_pct ?? 25} onChange={v => updateField('futures_long_tp2_gain_pct', v)}
                        min={5} max={100} step={1} prefix="+" suffix="% gain" disabled={flDisabled} width={65}
                      />
                    </div>
                    <Warn show={(config.futures_long_tp1_gain_pct ?? 10) >= (config.futures_long_tp2_gain_pct ?? 25)} text="TP1 target must be less than TP2 target" color="#ef4444" />
                  </div>
                  <div className="config-item" style={{ gridColumn: '1 / -1' }}>
                    <div className="config-label">Runner <Tip text={tips.futures_long_runner} /></div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: '#3b82f6' }}>
                      {flRunnerPct}%
                      <span style={{ fontSize: 13, fontWeight: 400, color: '#71717a', marginLeft: 8 }}>rides trailing stop</span>
                    </div>
                    <Warn show={flRunnerPct < 20} text="Runner below 20% limits upside potential" />
                    <Warn show={flRunnerPct <= 0} text="TP1 + TP2 must total less than 100%" color="#ef4444" />
                  </div>
                </div>
              </div>
            </div>

            {/* Risk */}
            <div className="card" style={{ gridColumn: '1 / -1' }}>
              <div className="card-header">
                <div className="card-title">Risk</div>
              </div>
              <div className="card-body" style={{ opacity: flDisabled ? 0.5 : 1 }}>
                <div className="config-grid">
                  <div className="config-item">
                    <div className="config-label">Risk Per Trade <Tip text={tips.futures_long_risk} /></div>
                    <NumInput
                      value={config.futures_long_risk_per_trade_pct ?? 2.0} onChange={v => updateField('futures_long_risk_per_trade_pct', v)}
                      min={0.5} max={10} step={0.5} suffix="%" disabled={flDisabled}
                    />
                    <div className="config-desc">Equity risked per leveraged long position</div>
                    <Warn show={(config.futures_long_risk_per_trade_pct ?? 2) > 5} text="High risk per trade — significant drawdowns possible" color="#ef4444" />
                    <Warn show={(config.futures_long_risk_per_trade_pct ?? 2) > 2 && (config.futures_long_leverage ?? 1) >= 2} text="Combined with leverage, effective risk is amplified" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── UAlgo Futures Short ── */}
      {activeTab === 'futures-short' && strategy === 'ualgo' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#e4e4e7' }}>
                  Enable Futures Short Trading <Tip text={tips.short_enabled} />
                </div>
                <div style={{ fontSize: 13, color: '#a1a1aa', marginTop: 2 }}>
                  {config.short_enabled ? 'UAlgo will open short positions during bear markets via Coinbase CFM perps' : 'Disabled — bot will not open short positions.'}
                </div>
              </div>
              <Toggle checked={config.short_enabled} onChange={v => updateField('short_enabled', v)} disabled={readOnly} />
            </div>
          </div>
          <div className="grid-equal">
            <div className="card">
              <div className="card-header"><div className="card-title">Short Settings</div></div>
              <div className="card-body">
                <div className="config-grid">
                  <div className="config-item">
                    <div className="config-label">Leverage <Tip text={tips.short_leverage} /></div>
                    <NumInput value={config.short_leverage ?? 1} onChange={v => updateField('short_leverage', v)} min={1} max={3} step={0.5} suffix="x" disabled={readOnly || !config.short_enabled} />
                  </div>
                  <div className="config-item">
                    <div className="config-label">Risk Per Trade <Tip text={tips.short_risk} /></div>
                    <NumInput value={config.short_risk_per_trade_pct ?? 2} onChange={v => updateField('short_risk_per_trade_pct', v)} min={0.5} max={5} step={0.5} suffix="%" disabled={readOnly || !config.short_enabled} />
                  </div>
                  <div className="config-item">
                    <div className="config-label">Bear Filter <Tip text={tips.bear_filter} /></div>
                    <Toggle checked={config.bear_filter_enabled} onChange={v => updateField('bear_filter_enabled', v)} disabled={readOnly || !config.short_enabled} />
                    <div className="config-desc">Death cross required for short entries</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-header"><div className="card-title">Short Signal Thresholds</div></div>
              <div className="card-body">
                <div className="config-grid">
                  <div className="config-item">
                    <div className="config-label">Short Stop Loss <Tip text="Fixed percentage stop loss above short entry" /></div>
                    <NumInput value={sp.short_sl_pct ?? 2.0} onChange={v => updateStrategyParam('short_sl_pct', v)} min={0.5} max={10} step={0.5} suffix="%" disabled={readOnly || !config.short_enabled} />
                  </div>
                  <div className="config-item">
                    <div className="config-label">Short RSI Sell Threshold <Tip text="RSI must be above this for short entries (overbought)" /></div>
                    <NumInput value={sp.short_rsi_sell_threshold ?? 60} onChange={v => updateStrategyParam('short_rsi_sell_threshold', Math.round(v))} min={40} max={80} step={5} disabled={readOnly || !config.short_enabled} />
                  </div>
                  <div className="config-item">
                    <div className="config-label">Short CMO Sell Threshold <Tip text="CMO must be below this for short entries (bearish momentum)" /></div>
                    <NumInput value={sp.short_cmo_sell_threshold ?? -10} onChange={v => updateStrategyParam('short_cmo_sell_threshold', Math.round(v))} min={-50} max={50} step={5} disabled={readOnly || !config.short_enabled} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Donchian Futures (Short) ── */}
      {activeTab === 'futures-short' && strategy === 'donchian' && (
        <div>
          {/* Master toggle */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#e4e4e7' }}>
                  Enable Futures Short Trading <Tip text={tips.short_enabled} />
                </div>
                <div style={{ fontSize: 13, color: '#a1a1aa', marginTop: 2 }}>
                  {config.short_enabled
                    ? 'Bot will open short positions via Coinbase CFM perpetual futures during bear markets'
                    : 'Disabled — bot will not open short positions. Enable to profit from downtrends.'}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: config.short_enabled ? '#a855f7' : '#71717a', fontWeight: 600 }}>
                  {config.short_enabled ? 'ON' : 'OFF'}
                </span>
                <Toggle checked={config.short_enabled ?? true} onChange={v => updateField('short_enabled', v)} disabled={readOnly} />
              </div>
            </div>
            <Warn show={!config.short_enabled} text="Futures short trading disabled. The bot will not open short positions." />
          </div>

          <div className="grid-equal">
            {/* Leverage */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">Leverage</div>
              </div>
              <div className="card-body" style={{ opacity: shortDisabled ? 0.5 : 1 }}>
                <div className="config-grid">
                  <div className="config-item">
                    <div className="config-label">Leverage Multiplier <Tip text={tips.short_leverage} /></div>
                    <NumInput
                      value={config.short_leverage ?? 1.0} onChange={v => updateField('short_leverage', v)}
                      min={1.0} max={3.0} step={0.5} suffix="x" disabled={shortDisabled}
                    />
                    <div className="config-desc">Short position size multiplier (1x = no leverage)</div>
                    <Warn show={(config.short_leverage ?? 1) >= 2 && (config.short_leverage ?? 1) < 3} text="2x leverage — short squeezes are violent, use caution" />
                    <Warn show={(config.short_leverage ?? 1) >= 3} text="Maximum leverage — only for confirmed bear markets with death cross" color="#ef4444" />
                  </div>
                  <div className="config-item">
                    <div className="config-label">Exchange</div>
                    <div className="config-value" style={{ fontSize: 14 }}>Coinbase CFM</div>
                    <div className="config-desc">CFTC-regulated perpetual futures, 0.06% taker fee, isolated margin</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <div className="card-title">Entry Conditions</div>
                <span className="badge badge-gray">Locked</span>
              </div>
              <div className="card-body" style={{ opacity: shortDisabled ? 0.5 : 1 }}>
                <div className="config-grid">
                  <div className="config-item">
                    <div className="config-label">Donchian Breakdown <Tip text={tips.short_entry_period} /></div>
                    <div className="config-value">{config.short_entry_period ?? 10} days</div>
                    <div className="config-desc">Breakdown below N-day low</div>
                  </div>
                  <div className="config-item">
                    <div className="config-label">Volume Multiplier <Tip text={tips.short_volume_mult} /></div>
                    <div className="config-value">{config.short_volume_mult ?? 2.0}x</div>
                    <div className="config-desc">Volume must exceed N x 20-day avg</div>
                  </div>
                  <div className="config-item">
                    <div className="config-label">EMA Trend Filter</div>
                    <div className="config-value">21-day</div>
                    <div className="config-desc">Price must be below EMA(21)</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <div className="card-title">Bear Market Filter</div>
              </div>
              <div className="card-body" style={{ opacity: shortDisabled ? 0.5 : 1 }}>
                <div className="config-grid">
                  <div className="config-item">
                    <div className="config-label">Bear Filter (Death Cross) <Tip text={tips.bear_filter} /></div>
                    <Toggle
                      checked={config.bear_filter_enabled ?? true}
                      onChange={v => updateField('bear_filter_enabled', v)}
                      disabled={shortDisabled}
                    />
                    <div className="config-desc">
                      {config.bear_filter_enabled
                        ? 'Short entries only when BTC has death cross'
                        : 'Short entries allowed regardless of BTC trend'}
                    </div>
                    <Warn
                      show={!config.bear_filter_enabled && config.short_enabled}
                      text="Bear filter disabled — shorts allowed in bull markets. Backtests show 22% DD without vs 9% DD with death cross filter."
                      color="#ef4444"
                    />
                  </div>
                  <div className="config-item">
                    <div className="config-label">How It Works</div>
                    <div style={{ fontSize: 13, color: '#a1a1aa', lineHeight: 1.5 }}>
                      Requires SMA(50) &lt; SMA(200) <strong style={{ color: '#e4e4e7' }}>and</strong> BTC &lt; SMA(200) (death cross).
                      Without this, short entries are blocked.
                    </div>
                    <div className="config-desc">See Bot Control for current bear filter status</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card" style={{ gridColumn: '1 / -1' }}>
              <div className="card-header">
                <div className="card-title">Exit Rules</div>
              </div>
              <div className="card-body" style={{ opacity: shortDisabled ? 0.5 : 1 }}>
                <div className="config-grid">
                  <div className="config-item">
                    <div className="config-label">ATR Inverted Trailing <Tip text={tips.short_atr_mult} /></div>
                    <NumInput
                      value={config.short_atr_mult ?? 2.0} onChange={v => updateField('short_atr_mult', v)}
                      min={1.0} max={6} step={0.5} suffix="x ATR(14)" disabled={shortDisabled}
                    />
                    <div className="config-desc">Trails above lowest price (inverted)</div>
                  </div>
                  <div className="config-item">
                    <div className="config-label">N-Day High Exit <Tip text={tips.short_exit_period} /></div>
                    <NumInput
                      value={config.short_exit_period ?? 15} onChange={v => updateField('short_exit_period', Math.round(v))}
                      min={5} max={30} step={1} suffix="days" disabled={shortDisabled}
                    />
                    <div className="config-desc">Close above N-day highest high</div>
                  </div>
                  <div className="config-item">
                    <div className="config-label">Emergency Stop <Tip text={tips.short_emergency_stop} /></div>
                    <NumInput
                      value={config.short_emergency_stop_pct ?? 18} onChange={v => updateField('short_emergency_stop_pct', v)}
                      min={5} max={30} step={1} suffix="%" disabled={shortDisabled}
                    />
                    <div className="config-desc">Hard ceiling above entry price</div>
                    <Warn show={(config.short_emergency_stop_pct ?? 18) < 10} text="Very tight — may cause premature exits" />
                  </div>
                  <div className="config-item">
                    <div className="config-label">Max Hold Days <Tip text={tips.short_max_hold} /></div>
                    <NumInput
                      value={config.short_max_hold_days ?? 25} onChange={v => updateField('short_max_hold_days', Math.round(v))}
                      min={7} max={90} step={1} suffix="days" disabled={shortDisabled}
                    />
                    <div className="config-desc">Force-close after N days</div>
                  </div>
                  <div className="config-item">
                    <div className="config-label">Partial TP1 <Tip text={tips.short_tp1} /></div>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <NumInput
                        value={config.short_tp1_size_pct ?? 25} onChange={v => updateField('short_tp1_size_pct', v)}
                        min={5} max={50} step={5} suffix="%" disabled={shortDisabled} width={65}
                      />
                      <span style={{ color: '#71717a', fontSize: 13 }}>at</span>
                      <NumInput
                        value={config.short_tp1_gain_pct ?? 10} onChange={v => updateField('short_tp1_gain_pct', v)}
                        min={3} max={50} step={1} prefix="-" suffix="% drop" disabled={shortDisabled} width={65}
                      />
                    </div>
                    <div className="config-desc">Cover a portion when price drops this far</div>
                  </div>
                  <div className="config-item">
                    <div className="config-label">Partial TP2 <Tip text={tips.short_tp2} /></div>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <NumInput
                        value={config.short_tp2_size_pct ?? 25} onChange={v => updateField('short_tp2_size_pct', v)}
                        min={5} max={50} step={5} suffix="%" disabled={shortDisabled} width={65}
                      />
                      <span style={{ color: '#71717a', fontSize: 13 }}>at</span>
                      <NumInput
                        value={config.short_tp2_gain_pct ?? 25} onChange={v => updateField('short_tp2_gain_pct', v)}
                        min={5} max={100} step={1} prefix="-" suffix="% drop" disabled={shortDisabled} width={65}
                      />
                    </div>
                    <Warn show={(config.short_tp1_gain_pct ?? 10) >= (config.short_tp2_gain_pct ?? 25)} text="TP1 target must be less than TP2 target" color="#ef4444" />
                  </div>
                  <div className="config-item" style={{ gridColumn: '1 / -1' }}>
                    <div className="config-label">Runner <Tip text={tips.short_runner} /></div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: '#ef4444' }}>
                      {shortRunnerPct}%
                      <span style={{ fontSize: 13, fontWeight: 400, color: '#71717a', marginLeft: 8 }}>rides inverted trailing stop</span>
                    </div>
                    <Warn show={shortRunnerPct < 20} text="Runner below 20% limits downside capture" />
                    <Warn show={shortRunnerPct <= 0} text="TP1 + TP2 must total less than 100%" color="#ef4444" />
                  </div>
                </div>
              </div>
            </div>

            <div className="card" style={{ gridColumn: '1 / -1' }}>
              <div className="card-header">
                <div className="card-title">Risk</div>
              </div>
              <div className="card-body" style={{ opacity: shortDisabled ? 0.5 : 1 }}>
                <div className="config-grid">
                  <div className="config-item">
                    <div className="config-label">Risk Per Short Trade <Tip text={tips.short_risk} /></div>
                    <NumInput
                      value={config.short_risk_per_trade_pct ?? 2.0} onChange={v => updateField('short_risk_per_trade_pct', v)}
                      min={0.5} max={10} step={0.5} suffix="%" disabled={shortDisabled}
                    />
                    <div className="config-desc">Equity risked per short position</div>
                    <Warn show={(config.short_risk_per_trade_pct ?? 2) > 5} text="High risk per trade — significant drawdowns possible" color="#ef4444" />
                    <Warn show={(config.short_risk_per_trade_pct ?? 2) > 2 && (config.short_leverage ?? 1) >= 2} text="Combined with leverage, effective risk is amplified" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Position Sizing ── */}
      {activeTab === 'sizing' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Position Sizing</div>
          </div>
          <div className="card-body">
            <div className="config-grid">
              <div className="config-item">
                <div className="config-label">Risk Per Trade (Long) <Tip text={tips.risk_per_trade} /></div>
                <NumInput
                  value={config.risk_per_trade_pct} onChange={v => updateField('risk_per_trade_pct', v)}
                  min={0.5} max={10} step={0.5} suffix="%" disabled={readOnly}
                />
                <div className="config-desc">Percentage of equity risked per spot position</div>
                <Warn show={config.starting_capital < 2000 && config.risk_per_trade_pct < 5} text="With a small account under $2K, consider 5% risk for meaningful position sizes" />
                <Warn show={config.starting_capital >= 10000 && config.risk_per_trade_pct > 3} text="With $10K+, 1-2% risk provides better drawdown control" />
                <Warn show={config.risk_per_trade_pct > 5} text="High risk per trade — significant drawdowns possible" color="#ef4444" />
              </div>
              <div className="config-item">
                <div className="config-label">Max Concurrent Positions <Tip text={tips.max_positions} /></div>
                <NumInput
                  value={config.max_positions} onChange={v => updateField('max_positions', Math.round(v))}
                  min={2} max={6} step={1} disabled={readOnly}
                />
                <div className="config-desc">Shared pool across longs + shorts</div>
                <Warn show={config.max_positions <= 2} text="Low diversification — portfolio concentrated in 1-2 positions" />
                <Warn show={config.max_positions >= 5} text="Capital spread thin — each position gets less allocation" />
              </div>
              <div className="config-item">
                <div className="config-label">Pyramid Risk <Tip text={tips.pyramid_risk} /></div>
                <NumInput
                  value={config.pyramid_risk_pct} onChange={v => updateField('pyramid_risk_pct', v)}
                  min={0.5} max={5} step={0.5} suffix="%" disabled={readOnly}
                />
                <div className="config-desc">Risk on pyramid add (typically half of initial)</div>
                <Warn show={config.pyramid_risk_pct > config.risk_per_trade_pct} text="Pyramid risk exceeds initial trade risk — unusual configuration" />
              </div>
              <div className="config-item">
                <div className="config-label">Starting Capital <Tip text={tips.starting_capital} /></div>
                <NumInput
                  value={config.starting_capital} onChange={v => updateField('starting_capital', v)}
                  min={100} max={1000000} step={100} prefix="$" disabled={readOnly}
                />
                <div className="config-desc">Paper trading allocation</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Coins ── */}
      {activeTab === 'coins' && (
        <div className="grid-3col">
          <div className="card">
            <div className="card-header">
              <div className="card-title">Spot Coins</div>
              <span className="badge badge-green">{coinSymbols.length} coins</span>
            </div>
            <div className="card-body">
              <div className="grid-2col" style={{ gap: 12 }}>
                {coinSymbols.map((coin: string) => (
                  <div key={coin} className="config-item" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#e4e4e7' }}>{coin}</div>
                    <div className="config-desc">Spot Long</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header">
              <div className="card-title">Futures Long</div>
              <span className="badge" style={{ background: 'rgba(59,130,246,0.15)', color: '#3b82f6' }}>{flCoinSymbols.length} coins</span>
            </div>
            <div className="card-body" style={{ opacity: config.futures_long_enabled ? 1 : 0.5 }}>
              <div className="grid-2col" style={{ gap: 12 }}>
                {flCoinSymbols.map((coin: string) => (
                  <div key={coin} className="config-item" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#e4e4e7' }}>{coin}</div>
                    <div className="config-desc">Perp Long</div>
                  </div>
                ))}
              </div>
              {!config.futures_long_enabled && (
                <div style={{ fontSize: 12, color: '#71717a', textAlign: 'center', marginTop: 8 }}>
                  Futures long disabled — enable on Futures Long tab
                </div>
              )}
            </div>
          </div>
          <div className="card">
            <div className="card-header">
              <div className="card-title">Futures Short</div>
              <span className="badge" style={{ background: 'rgba(168,85,247,0.15)', color: '#a855f7' }}>{shortCoinSymbols.length} coins</span>
            </div>
            <div className="card-body" style={{ opacity: config.short_enabled ? 1 : 0.5 }}>
              <div className="grid-2col" style={{ gap: 12 }}>
                {shortCoinSymbols.map((coin: string) => (
                  <div key={coin} className="config-item" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#e4e4e7' }}>{coin}</div>
                    <div className="config-desc">Perp Short</div>
                  </div>
                ))}
              </div>
              {!config.short_enabled && (
                <div style={{ fontSize: 12, color: '#71717a', textAlign: 'center', marginTop: 8 }}>
                  Futures short disabled — enable on Futures Short tab
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Strategy;
